import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { IMAGES } from "../lib/images";
import { LOGO_FULL } from "../lib/brand";
import LocationMap from "../components/LocationMap";
import LaunchNotice from "../components/LaunchNotice";
import Reviews from "../components/Reviews";
import useSEO from "../lib/useSEO";

export default function Home() {
  const { t } = useLang();

  useSEO({
    title: "Tæste — Pizza Catering Hoyerswerda & Lausitz | Catering für Hochzeiten, Firmenfeiern und Events",
    description:
      "Tæste ist euer mobiles Pizza Catering in Hoyerswerda, Lohsa und der Lausitz. Handgemachte Pizza aus dem mobilen Pizzaofen für Hochzeiten, Geburtstage, Firmenfeiern und Events.",
    keywords:
      "Pizza Catering Hoyerswerda, Pizza Catering Lausitz, Catering Hoyerswerda, Catering Lausitz, Catering Lohsa, mobiles Pizza Catering, mobiler Pizzaofen Catering, Catering Events, Catering Firmenfeier, Catering Geburtstag",
    path: "/",
  });

  return (
    <div data-testid="home-page" id="top" className="overflow-hidden">
      {/* HERO — responsive: kompakt auf Mobile, voll Höhe auf Desktop */}
      <section
        data-testid="hero-section"
        className="relative w-full overflow-hidden bg-[rgb(223,209,193)] flex items-center justify-center
                   pt-20 pb-4 sm:pb-8 md:pb-16 lg:pb-20
                   min-h-[62svh] sm:min-h-[78vh] md:min-h-[88vh] lg:min-h-screen lg:h-screen"
      >
        <img
          src="/taeste-hero.png?v=5"
          alt="Tæste — Catering & Streetfood"
          data-testid="hero-logo"
          className="block max-w-full max-h-full w-auto h-auto fade-up"
        />
      </section>

      {/* Hero-Subtext — fließt aus dem Stein-Hero raus */}
      <section className="relative bg-[hsl(var(--background))] -mt-6 sm:-mt-12 md:-mt-24 pt-4 pb-20 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="eyebrow fade-up" data-testid="hero-eyebrow">{t.home.eyebrow}</p>
          <h1 className="brand-mark text-4xl sm:text-6xl md:text-7xl mt-5 leading-[1.05] fade-up fade-up-d1" data-testid="hero-slogan">
            {t.home.hero_slogan}
          </h1>
          <p className="font-display italic text-xl sm:text-2xl md:text-3xl mt-4 text-[hsl(var(--muted-foreground))] fade-up fade-up-d2" data-testid="hero-title">
            {t.home.hero_title_a} {t.home.hero_title_b}
          </p>
          <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto fade-up fade-up-d2">
            {t.home.hero_sub}
          </p>
          <div className="mt-14 md:mt-16 max-w-2xl mx-auto text-left fade-up fade-up-d3">
            <LaunchNotice
              testId="home-launch-notice"
              eyebrow={t.contact.notice_eyebrow}
              body={t.contact.notice_body}
            />
          </div>
        </div>
      </section>

      {/* ÜBER UNS — Annabell & Edgar Teaser */}
      <section id="ueber-uns" className="relative max-w-[1300px] mx-auto px-6 lg:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-16 items-center scroll-mt-24">
        <div className="md:col-span-5 relative">
          <div className="relative aspect-[3/4] overflow-hidden bg-[hsl(var(--secondary))]" style={{ borderRadius: "38px" }}>
            <img src="/annabell-edgar-wald.webp?v=9" alt="Annabell & Edgar im Kiefernwald" data-testid="home-about-photo" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-7 md:pl-8">
          <p className="eyebrow">{t.home.intro_eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">{t.home.intro_title}</h2>
          <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed max-w-xl">
            {t.home.intro_body}
          </p>
          <Link to="/ueber-uns" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold border-b border-[hsl(var(--primary))] pb-1">
            {t.home.intro_link} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* LEISTUNGEN — Pizza im Mittelpunkt */}
      <section id="leistungen" className="relative bg-[hsl(var(--secondary))]/20 py-24 md:py-32 scroll-mt-24 overflow-hidden">
        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="eyebrow">{t.home.services_eyebrow}</p>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-tight">{t.home.services_title}</h2>
            <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))]">{t.home.services_lead}</p>
          </div>

          {/* 01 · UNSERE GENUSSKARTE (Vorspeisen · Pizzen · Desserts) */}
          <div className="grid md:grid-cols-12 gap-12 items-center mb-24">
            <div className="md:col-span-7 order-2 md:order-1">
              <span className="font-display text-xl text-[hsl(var(--brand-gold))]">01</span>
              <h3 className="font-display text-3xl md:text-4xl mt-2">{t.home.services_pizza_title}</h3>
              <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed max-w-xl">{t.home.services_pizza_body}</p>
              <Link to="/catering" data-testid="home-pizza-link" className="mt-7 inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold border-b border-[hsl(var(--primary))] pb-1">
                {t.home.services_pizza_link} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <div className="aspect-square blob-organic overflow-hidden">
                <img src={IMAGES.edgarPizza} alt="Edgar mit frischer Pizza" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          {/* 02 · CATERING-KONZEPTE */}
          <div className="border-t border-[hsl(var(--border))] pt-16" data-testid="catering-concepts">
            <span className="font-display text-xl text-[hsl(var(--brand-gold))]">02</span>
            <p className="eyebrow mt-2">{t.home.concepts_eyebrow}</p>
            <h3 className="font-display text-3xl md:text-5xl mt-3 mb-12 leading-tight">{t.home.concepts_title}</h3>
            <div className="grid md:grid-cols-3 gap-10 md:gap-12">
              {t.home.concepts.map((c, i) => (
                <article key={c.name} data-testid={`concept-${i}`} className="flex flex-col">
                  <span className="font-display text-xl text-[hsl(var(--brand-gold))]">0{i + 1}</span>
                  <h4 className="font-display text-2xl md:text-3xl mt-2 leading-snug">{c.name}</h4>
                  <p className="mt-2 text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(var(--primary))]">{c.subtitle}</p>
                  <p className="mt-5 text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{c.body}</p>
                  {c.options && c.options.length > 0 && (
                    <div className="mt-6">
                      <p className="eyebrow mb-3">{c.options_title}</p>
                      <ul className="space-y-1.5 text-[hsl(var(--muted-foreground))]">
                        {c.options.map((opt) => (
                          <li key={opt} className="flex items-start gap-2"><span className="text-[hsl(var(--primary))]">·</span><span>{opt}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>

          {/* 03 · UNSER SETUP */}
          <div className="mt-24 md:mt-32 border-t border-[hsl(var(--border))] pt-16" data-testid="services-setup">
            <span className="font-display text-xl text-[hsl(var(--brand-gold))]">03</span>
            <p className="eyebrow mt-2">{t.home.services_setup_title}</p>
            <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">{t.home.services_setup_body}</p>
          </div>

          {/* ANLÄSSE */}
          <div className="mt-20 md:mt-24" data-testid="occasions">
            <h3 className="font-display text-2xl md:text-3xl mb-6">{t.home.occasions_title}</h3>
            <div className="flex flex-wrap gap-2">
              {t.home.occasions.map((o, i) => (
                <span key={o} data-testid={`occasion-chip-${i}`} className="px-4 py-2 text-xs tracking-widest uppercase font-semibold border border-[hsl(var(--border))] bg-[hsl(var(--background))] pill">
                  {o}
                </span>
              ))}
            </div>
          </div>

          {/* 04 · ANFRAGE & PREISGESTALTUNG */}
          <div className="mt-24 md:mt-32 border-t border-[hsl(var(--border))] pt-16" data-testid="home-pricing">
            <span className="font-display text-xl text-[hsl(var(--brand-gold))]">04</span>
            <p className="eyebrow mt-2">{t.home.pricing_eyebrow}</p>
            <h3 className="font-display text-3xl md:text-5xl mt-3 leading-tight max-w-3xl">{t.home.pricing_title}</h3>
            <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">{t.home.pricing_body}</p>
            <ul className="mt-8 space-y-2 max-w-3xl">
              {t.home.pricing_points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[hsl(var(--foreground))]"><span className="text-[hsl(var(--primary))] mt-1">·</span><span>{p}</span></li>
              ))}
            </ul>
            <a href="#kontakt" data-testid="home-pricing-cta" className="mt-10 inline-flex items-center gap-2 px-7 py-4 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill">
              {t.home.pricing_cta} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* GALERIE */}
      <section id="galerie" className="scroll-mt-24 max-w-[1300px] mx-auto px-6 lg:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="eyebrow">{t.gallery.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-6xl mt-3">{t.gallery.title}</h2>
          </div>
          <Link to="/galerie" data-testid="home-gallery-link" className="inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold border-b border-[hsl(var(--primary))] pb-1">
            {t.home.gallery_link} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <figure className="md:col-span-12 aspect-[16/10] overflow-hidden blob-organic bg-[hsl(var(--secondary))]"><img src="/gallery/g6.png" alt="Unser abendliches Catering-Setup mit Pavillon, Lichterketten und Servierstationen" loading="lazy" className="w-full h-full object-cover" /></figure>
          <figure className="md:col-span-8 aspect-[4/3] overflow-hidden blob-organic-2 bg-[hsl(var(--secondary))]"><img src="/gallery/g1.png" alt="Tæste Pavillon-Setup mit Pizzaofen und Servierstation" loading="lazy" className="w-full h-full object-cover" /></figure>
          <figure className="md:col-span-4 aspect-[3/4] overflow-hidden blob-organic bg-[hsl(var(--secondary))]"><img src="/gallery/g2.jpeg" alt="Die Bunte, unsere vegane Pizza mit mediterranem Grillgemüse" loading="lazy" className="w-full h-full object-cover" /></figure>
          <figure className="md:col-span-6 aspect-[4/3] overflow-hidden blob-organic bg-[hsl(var(--secondary))]"><img src="/gallery/g4.png" alt="Fertigstellung einer Pizza mit Pistaziencreme und Mortadella" loading="lazy" className="w-full h-full object-cover" /></figure>
          <figure className="md:col-span-6 aspect-[4/3] overflow-hidden blob-organic-2 bg-[hsl(var(--secondary))]"><img src="/gallery/g5.jpeg" alt="Servierlinie mit frisch gebackenen Pizzen unter Wärmelampen" loading="lazy" className="w-full h-full object-cover" /></figure>
        </div>
      </section>

      {/* KUNDENREZENSIONEN */}
      <Reviews />

      {/* KONTAKT TEASER */}
      <section id="kontakt" className="scroll-mt-24 bg-[hsl(var(--secondary))]/40 py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <p className="eyebrow">{t.contact.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-6xl mt-3 leading-tight">{t.contact.title}</h2>
          <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">{t.contact.body}</p>
          <Link to="/kontakt" data-testid="home-contact-cta" className="mt-10 inline-flex items-center gap-2 px-7 py-4 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill">
            {t.nav.cta} <ArrowRight size={14} />
          </Link>

          {/* STANDORT-KARTE */}
          <div className="mt-16 md:mt-20 text-left max-w-[1000px] mx-auto" data-testid="home-location">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div>
                <p className="eyebrow">{t.contact.location_title}</p>
                <h3 className="font-display text-2xl md:text-4xl mt-2 leading-tight">{t.contact.info.location}</h3>
              </div>
              <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] max-w-md">{t.contact.location_lead}</p>
            </div>
            <div className="relative aspect-[16/9] overflow-hidden border border-border bg-[hsl(var(--secondary))]">
              <LocationMap testId="home-contact-map" zoom={11} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
